import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  FiArrowLeft,
  FiClock,
  FiCreditCard,
  FiGlobe,
  FiMail,
  FiMapPin,
  FiPhone,
  FiShield,
  FiTool,
} from "react-icons/fi";
import { FaFacebookF, FaInstagram } from "react-icons/fa";

import PageHero from "../../components/common/PageHero";
import PageSectionHeader from "../../components/common/PageSectionHeader";
import Reveal from "../../components/ui/Reveal";
import ScrollIndicator from "../Home/ScrollIndicator";

import { getCommerceBySlug } from "../../services/commercesServices";

import CommerceAdaptiveImage from "./components/CommerceAdaptiveImage";
import CommerceContactLink from "./components/CommerceContactLink";
import CommerceDetailSkeleton from "./components/CommerceDetailSkeleton";
import CommerceMapPanel from "./components/CommerceMapPanel";
import useScrollToTop from "../../hooks/useScollToTop";


import {
  COMMERCE_DAYS,
  normalizeCommerce,
} from "./utils/commercesUtils";

function InfoBlock({ icon: Icon, title, children }) {
  if (!children) return null;

  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950">
      <div className="flex items-center gap-3">
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-[#1277B9]/10 text-[#1277B9] dark:bg-[#1277B9]/20 dark:text-sky-300">
          <Icon className="h-4 w-4" />
        </span>
        <h3 className="font-bold text-slate-950 dark:text-white">{title}</h3>
      </div>

      <div className="mt-4 text-sm leading-7 text-slate-600 dark:text-slate-300">
        {children}
      </div>
    </div>
  );
}

function HorairesTable({ horaires }) {
  if (!horaires) return null;

  return (
    <Reveal>
      <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-950">
        <div className="flex items-center gap-3 border-b border-slate-200 bg-slate-50 px-5 py-4 dark:border-slate-800 dark:bg-slate-900">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-[#1277B9]/10 text-[#1277B9] dark:bg-[#1277B9]/20 dark:text-sky-300">
            <FiClock className="h-4 w-4" />
          </span>
          <h3 className="font-bold text-slate-950 dark:text-white">
            Horaires
          </h3>
        </div>

        <div className="divide-y divide-slate-200 dark:divide-slate-800">
          {COMMERCE_DAYS.map(([key, label]) => {
            const day = horaires[key];
            const am = day?.am || "";
            const pm = day?.pm || "";
            const text = am || pm ? [am, pm].filter(Boolean).join(" / ") : "Fermé";

            return (
              <div
                key={key}
                className="grid grid-cols-[110px_1fr] gap-3 px-5 py-3 text-sm"
              >
                <span className="font-bold text-slate-700 dark:text-slate-200">
                  {label}
                </span>
                <span className="text-slate-600 dark:text-slate-300">
                  {text}
                </span>
              </div>
            );
          })}
        </div>
      </section>
    </Reveal>
  );
}

export default function CommerceDetailPage() {
  useScrollToTop();
  const { slug } = useParams();

  const [loading, setLoading] = useState(true);
  const [commerce, setCommerce] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;

    (async () => {
      setLoading(true);
      setError("");

      try {
        const data = await getCommerceBySlug(slug);

        if (!mounted) return;

        const normalized = data ? normalizeCommerce(data) : null;
        setCommerce(normalized);

        if (normalized?.title) {
          document.title = `${normalized.title} — Commerces`;
        }
      } catch (err) {
        console.error("Erreur chargement commerce:", err);
        if (mounted) {
          setCommerce(null);
          setError("LOAD_ERROR");
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [slug]);

  const tags = useMemo(() => {
    return String(commerce?.tags || "")
      .split(",")
      .map((tag) => tag.trim())
      .filter(Boolean);
  }, [commerce?.tags]);

  return (
    <>
      <PageHero
        title={commerce?.title || "Commerce"}
        imageUrl={commerce?.image || "/img/no-image.jpg"}
        crumbs={[
          { label: "Accueil", href: "/" },
          { label: "Ma Ville" },
          { label: "Commerces", href: "/commerces" },
          { label: commerce?.title || "Détail" },
        ]}
      />

      <ScrollIndicator />

      <main className="mx-auto max-w-[1180px] px-4 pb-16 pt-6 sm:px-6 md:pt-8">
        <Reveal>
          <Link
            to="/commerces"
            className="inline-flex min-h-11 items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 transition hover:border-[#1277B9]/40 hover:bg-sky-50 hover:text-[#1277B9] dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100 dark:hover:bg-slate-900"
          >
            <FiArrowLeft className="h-4 w-4" />
            Retour aux commerces
          </Link>
        </Reveal>

        {loading && <CommerceDetailSkeleton />}

        {!loading && !commerce && (
          <div className="mt-6 rounded-[2rem] border border-dashed border-slate-300 bg-white px-6 py-12 text-center shadow-sm dark:border-slate-700 dark:bg-slate-950">
            <h2 className="text-xl font-bold text-slate-950 dark:text-white">
              Commerce introuvable
            </h2>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
              Ce commerce n’existe pas ou n’est plus disponible.
            </p>
          </div>
        )}

        {!loading && commerce && (
          <>
            <div className="mt-5">
              <PageSectionHeader
                eyebrow={commerce.category || "Commerce local"}
                title={commerce.title}
                description={commerce.descriptionPreview}
              >
                {commerce.logo && (
                  <div className="mx-auto h-24 w-24 overflow-hidden rounded-3xl border border-slate-200 bg-white p-2 shadow-sm dark:border-slate-800 dark:bg-slate-950 lg:mx-0">
                    <img
                      src={commerce.logo}
                      alt={`Logo ${commerce.title}`}
                      className="h-full w-full object-contain"
                    />
                  </div>
                )}
              </PageSectionHeader>
            </div>

            <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_360px]">
              <div className="space-y-6">
                <Reveal>
                  <CommerceAdaptiveImage
                    src={commerce.image}
                    alt={commerce.title}
                    className="h-[360px] rounded-[2rem] border border-slate-200 shadow-sm dark:border-slate-800"
                    rounded="rounded-[2rem]"
                  />
                </Reveal>

                {(commerce.category || tags.length > 0) && (
                  <Reveal>
                    <div className="flex flex-wrap gap-2 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950">
                      {commerce.category && (
                        <span className="inline-flex rounded-full bg-[#1277B9]/10 px-3 py-1 text-sm font-bold text-[#1277B9] dark:bg-[#1277B9]/20 dark:text-sky-300">
                          {commerce.category}
                        </span>
                      )}

                      {tags.map((tag) => (
                        <span
                          key={tag}
                          className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-sm font-bold text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </Reveal>
                )}

                {commerce.descriptionRaw && (
                  <Reveal>
                    <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950 sm:p-6">
                      <h2 className="text-xl font-bold text-slate-950 dark:text-white">
                        Présentation
                      </h2>

                      <div
                        className="prose prose-sm mt-4 max-w-none leading-relaxed text-slate-700 prose-a:text-[#1277B9] prose-img:rounded-2xl dark:prose-invert dark:text-slate-200 sm:prose-base"
                        dangerouslySetInnerHTML={{
                          __html: commerce.descriptionRaw,
                        }}
                      />
                    </section>
                  </Reveal>
                )}

                <HorairesTable horaires={commerce.horaires} />

                {commerce.hasMap && <CommerceMapPanel commerce={commerce} />}
              </div>

              <aside className="space-y-5">
                <Reveal>
                  <div className="rounded-[2rem] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950">
                    <h3 className="text-lg font-bold text-slate-950 dark:text-white">
                      Contact
                    </h3>

                    <div className="mt-4 flex flex-wrap gap-2">
                      <CommerceContactLink
                        href={commerce.phoneHref}
                        icon={FiPhone}
                        label={commerce.phone || "Téléphoner"}
                      />

                      <CommerceContactLink
                        href={commerce.mail ? `mailto:${commerce.mail}` : ""}
                        icon={FiMail}
                        label="Email"
                        variant={commerce.mail ? "primary" : "default"}
                      />

                      <CommerceContactLink
                        href={commerce.websiteUrl}
                        icon={FiGlobe}
                        label="Site internet"
                        external
                      />

                      <CommerceContactLink
                        href={commerce.facebookUrl}
                        icon={FaFacebookF}
                        label="Facebook"
                        external
                      />

                      <CommerceContactLink
                        href={commerce.instagramUrl}
                        icon={FaInstagram}
                        label="Instagram"
                        external
                      />
                    </div>

                    {commerce.address && (
                      <div className="mt-5 flex items-start gap-3 rounded-3xl border border-slate-200 bg-slate-50 p-4 text-sm font-semibold text-slate-600 dark:border-slate-800 dark:bg-slate-900/70 dark:text-slate-300">
                        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white text-[#1277B9] shadow-sm dark:bg-slate-950">
                          <FiMapPin className="h-4 w-4" />
                        </span>

                        {commerce.mapsHref ? (
                          <a
                            href={commerce.mapsHref}
                            target="_blank"
                            rel="noreferrer"
                            className="pt-2 hover:text-[#1277B9] hover:underline"
                          >
                            {commerce.address}
                          </a>
                        ) : (
                          <span className="pt-2">{commerce.address}</span>
                        )}
                      </div>
                    )}
                  </div>
                </Reveal>

                <InfoBlock icon={FiCreditCard} title="Moyens de paiement">
                  {commerce.moyensPaiement}
                </InfoBlock>

                <InfoBlock icon={FiTool} title="Services">
                  {commerce.services}
                </InfoBlock>

                <InfoBlock icon={FiShield} title="Accessibilité">
                  {commerce.accessibilite}
                </InfoBlock>
              </aside>
            </div>
          </>
        )}
      </main>
    </>
  );
}